import { NO_HANDLE, type EmoteKind } from "odyssey-shared";
import { readSetting, writeSetting, type SettingsStore } from "../settings.ts";
import { EMOTES_LAST_KEY, formatLast, openingPlace, parseLast, turnPage, wheelPages, wheelSteps, type KeptPlace, type WheelEntry, type WheelPage } from "./rules.ts";

/**
 * The emote wheel's state (P2.19-T8; ADR-114 decisions 1.2, 1.3, 2.2, 6.2 and 6.3). The wheel is a list in pages:
 * each page is one group of the emote table, showing only what the account's sets hold. The store holds whether the
 * gateway has offered emotes, whether the wheel is open, the page and the entry selected, and the page and entry last
 * sent from, kept per device. It decides nothing about an emote: it sends a kind, an id and a target, and the server
 * judges all three.
 *
 * The rules it follows are pure functions in `rules.ts`, tested without a page.
 */

/**
 * What the wheel shows: whether the gateway has offered emotes the wheel holds, whether it is open, its pages, and the
 * page and entry selected.
 */
export interface EmoteWheelSnapshot {
  readonly offered: boolean;
  readonly open: boolean;
  readonly pages: readonly WheelPage[];
  readonly page: number;
  readonly entry: number;
}

/**
 * Something a view reads the wheel from.
 */
export interface EmoteWheelSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => EmoteWheelSnapshot;
}

/**
 * Sends one `emote` on the current connection.
 *
 * @param kind The emote's kind.
 * @param id Its id within its kind.
 * @param target The entity handle an action names, or `NO_HANDLE` for none.
 * @returns False when nothing was sent.
 */
export type EmoteSender = (kind: EmoteKind, id: number, target: number) => boolean;

/**
 * What the store is made with: where the last place is kept (null for no store), whether the page refuses to open the
 * wheel now (its character frozen, dead or not yet in the world), and the handle of the target an action would name,
 * `NO_HANDLE` for none, read when an emote is sent.
 */
export interface EmoteWheelOptions {
  readonly settings: SettingsStore | null;
  readonly blocked: () => boolean;
  readonly target: () => number;
}

const CLOSED: EmoteWheelSnapshot = { offered: false, open: false, pages: [], page: 0, entry: 0 };

/**
 * The wheel's store.
 *
 * @example
 * const wheel = new EmoteWheelStore({ settings: localStore(), blocked: () => frozen(), target: () => NO_HANDLE });
 * wheel.bindSend((kind, id, target) => connection.sendEmote(kind, id, target));
 * wheel.receive(["base"]);
 * wheel.toggle();
 */
export class EmoteWheelStore implements EmoteWheelSource {
  private readonly listeners = new Set<() => void>();
  private snapshot: EmoteWheelSnapshot = CLOSED;
  private sender: EmoteSender | null = null;
  private kept: KeptPlace | null;
  private carry = 0;
  private entry = false;

  /**
   * Reads the place the device kept.
   *
   * @param options The settings store, the page's refusal and the target.
   */
  public constructor(private readonly options: EmoteWheelOptions) {
    this.kept = readSetting(EMOTES_LAST_KEY, parseLast, options.settings);
  }

  /**
   * Listens for the wheel changing. It is the same function for the store's life, so a React store subscribes once.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the wheel shows now. The same object until something changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): EmoteWheelSnapshot => this.snapshot;

  /**
   * Gives the wheel the way to send on whatever connection the scene holds.
   *
   * @param sender The sender.
   */
  public bindSend(sender: EmoteSender): void {
    this.sender = sender;
  }

  /**
   * Takes the gateway's `emotes`: the sets the account holds, which is also the gateway saying it speaks emotes
   * (ADR-114 decision 6.2). An open wheel stays open on the place it shows where the new pages still hold it, and
   * closes when the sets hold nothing the wheel shows.
   *
   * @param sets The set names.
   */
  public receive(sets: readonly string[]): void {
    const pages = wheelPages(sets);
    if (pages.length === 0) {
      this.ended();
      return;
    }
    const page = Math.min(this.snapshot.page, pages.length - 1);
    const entry = Math.min(this.snapshot.entry, (pages[page] as WheelPage).entries.length - 1);
    this.set({ offered: true, open: this.snapshot.open, pages, page, entry });
  }

  /**
   * The connection ended: the offer is taken back until the next join, and an open wheel closes.
   */
  public ended(): void {
    this.entry = false;
    this.carry = 0;
    this.set(CLOSED);
  }

  /**
   * Opens a closed wheel, or closes an open one.
   *
   * @returns Whether the wheel is open afterwards: false when it was closed, or could not open because the gateway
   *   has not offered emotes or the page refuses.
   */
  public toggle(): boolean {
    if (this.snapshot.open) {
      this.close();
      return false;
    }
    return this.open();
  }

  /**
   * Opens the wheel and leaves an open one open: the game menu's item, which never closes it.
   *
   * @returns Whether the wheel is open afterwards.
   */
  public show(): boolean {
    return this.snapshot.open || this.open();
  }

  /**
   * Opens the wheel, or enters one already open, for a player who moves by keyboard: the view takes the entry and
   * puts focus on the selected entry (ADR-114 decision 1.5). It never closes the wheel.
   *
   * @returns Whether the wheel is open afterwards.
   */
  public enter(): boolean {
    if (!this.snapshot.open && !this.open()) {
      return false;
    }
    this.entry = true;
    this.tell();
    return true;
  }

  /**
   * Whether the wheel was entered since this was last asked, which the view asks once it has drawn.
   *
   * @returns True once for each entry.
   */
  public takeEntry(): boolean {
    const entered = this.entry;
    this.entry = false;
    return entered;
  }

  /**
   * Closes the wheel.
   */
  public close(): void {
    this.entry = false;
    if (this.snapshot.open) {
      this.carry = 0;
      this.set({ ...this.snapshot, open: false });
    }
  }

  /**
   * Turns to the page after or before, wrapping, with its first entry selected.
   *
   * @param step 1 for the page after, -1 for the one before.
   */
  public turn(step: 1 | -1): void {
    if (this.snapshot.open) {
      this.showPage(turnPage(this.snapshot.page, step, this.snapshot.pages.length));
    }
  }

  /**
   * Goes to a page by its index, with its first entry selected.
   *
   * @param page The page's index among the pages shown; anything else is ignored.
   */
  public goTo(page: number): void {
    if (this.snapshot.open && Number.isInteger(page) && page >= 0 && page < this.snapshot.pages.length) {
      this.showPage(page);
    }
  }

  /**
   * Takes a mouse wheel's delta: the page turns one for each `WHEEL_NOTCH` of it, down for the page after and up for
   * the one before, wrapping as Q and E turn it, each turn selecting the page's first entry (the author, 2026-10-08:
   * "Turn the page (Recommended)").
   *
   * @param delta The delta in pixels, positive downward (`wheelDelta`).
   * @returns Whether the wheel is open and so took the event, whether or not the page turned.
   */
  public scroll(delta: number): boolean {
    if (!this.snapshot.open) {
      return false;
    }
    const { steps, carry } = wheelSteps(this.carry, delta);
    if (steps !== 0) {
      let page = this.snapshot.page;
      for (let turned = 0; turned < Math.abs(steps); turned += 1) {
        page = turnPage(page, steps > 0 ? 1 : -1, this.snapshot.pages.length);
      }
      this.showPage(page);
    }
    this.carry = carry;
    return true;
  }

  /**
   * Selects an entry of the page by its index.
   *
   * @param entry The entry's index on the page; anything else is ignored.
   */
  public select(entry: number): void {
    if (this.snapshot.open && Number.isInteger(entry) && entry >= 0 && entry < this.entries().length && entry !== this.snapshot.entry) {
      this.set({ ...this.snapshot, entry });
    }
  }

  /**
   * Sends the selected entry with the target held, keeps its page and entry for the next opening, and closes the
   * wheel (ADR-114 decision 2.4). The server judges the emote; a refusal comes back as a line of chat.
   *
   * @returns Whether it was sent: false for a closed wheel, with no sender bound, or when the connection took
   *   nothing, which leaves the wheel open.
   */
  public send(): boolean {
    const page = this.snapshot.pages[this.snapshot.page];
    const chosen = page?.entries[this.snapshot.entry];
    if (!this.snapshot.open || page === undefined || chosen === undefined || this.sender === null) {
      return false;
    }
    const held = this.options.target();
    const target = Number.isInteger(held) && held >= 0 && held < NO_HANDLE ? held : NO_HANDLE;
    if (!this.sender(chosen.kind, chosen.id, chosen.kind === "action" ? target : NO_HANDLE)) {
      return false;
    }
    this.kept = { page: page.name, kind: chosen.kind, id: chosen.id };
    writeSetting(EMOTES_LAST_KEY, formatLast(this.kept), this.options.settings);
    this.close();
    return true;
  }

  /**
   * Opens the wheel at the kept place.
   *
   * @returns Whether it opened.
   */
  private open(): boolean {
    if (!this.snapshot.offered || this.options.blocked()) {
      return false;
    }
    this.carry = 0;
    this.set({ ...this.snapshot, open: true, ...openingPlace(this.snapshot.pages, this.kept) });
    return true;
  }

  /**
   * Shows a page with its first entry selected.
   *
   * @param page The page's index.
   */
  private showPage(page: number): void {
    this.carry = 0;
    if (page !== this.snapshot.page || this.snapshot.entry !== 0) {
      this.set({ ...this.snapshot, page, entry: 0 });
    }
  }

  /**
   * The entries of the page shown.
   *
   * @returns The entries; none when the wheel has no pages.
   */
  private entries(): readonly WheelEntry[] {
    return this.snapshot.pages[this.snapshot.page]?.entries ?? [];
  }

  /**
   * Puts a snapshot in force and tells the listeners, unless it shows what the one in force does.
   *
   * @param next The snapshot.
   */
  private set(next: EmoteWheelSnapshot): void {
    const now = this.snapshot;
    if (next === now || (next.offered === now.offered && next.open === now.open && next.page === now.page && next.entry === now.entry && samePages(next.pages, now.pages))) {
      return;
    }
    this.snapshot = next;
    this.tell();
  }

  /**
   * Tells every listener.
   */
  private tell(): void {
    for (const listener of [...this.listeners]) {
      listener();
    }
  }
}

/**
 * Whether two lists of pages show the same entries.
 *
 * @param a One list.
 * @param b The other.
 * @returns True when every page and entry matches.
 */
function samePages(a: readonly WheelPage[], b: readonly WheelPage[]): boolean {
  return a === b || (a.length === b.length && a.every((page, at) => page.name === b[at]?.name && page.entries.length === b[at]?.entries.length && page.entries.every((entry, index) => entry.kind === b[at]?.entries[index]?.kind && entry.id === b[at]?.entries[index]?.id)));
}
